import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { lookupKellerProduct, type KellerLookupResult } from "@/lib/keller.functions";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Loader2, Search, RefreshCw, ZoomIn } from "lucide-react";
import ImageZoomViewer from "@/components/ui/ImageZoomViewer";
import { kellerImageUrl } from "@/lib/kellerXml";

export const Route = createFileRoute("/_adminLayout/admin/keller-test")({
  head: () => ({ meta: [
    { title: "Keller Marine API Test | Rex & Sons Admin" },
    { name: "description", content: "Admin-only Keller Marine parts lookup and product photo testing for Rex & Sons RV." },
    { property: "og:title", content: "Keller Marine API Test | Rex & Sons Admin" },
    { property: "og:description", content: "Admin-only Keller Marine parts lookup and product photo testing for Rex & Sons RV." },
    { property: "og:type", content: "website" },
    { name: "twitter:card", content: "summary" },
    { name: "robots", content: "noindex" },
  ] }),
  component: KellerTest,
});

const money = (n: number) => (n ? n.toLocaleString("en-US", { style: "currency", currency: "USD" }) : "—");

function KellerTest() {
  const lookup = useServerFn(lookupKellerProduct);
  const [part, setPart] = useState("5690100");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState<KellerLookupResult | null>(null);
  const [img, setImg] = useState(0);
  const [zoom, setZoom] = useState<number | null>(null);
  const [showRaw, setShowRaw] = useState(false);

  async function run(refresh = false) {
    if (!part.trim()) return;
    setBusy(true);
    setImg(0);
    try {
      setRes(await lookup({ data: { partNumber: part.trim(), refresh } }));
    } catch (e: any) {
      setRes({ product: null, error: e?.message ?? "Lookup failed", rawXml: "", cached: false, fetchedAt: new Date().toISOString() });
    } finally {
      setBusy(false);
    }
  }

  const p = res?.product;
  const images = p ? Array.from(new Set(
    (p.images.length ? p.images : [p.imageUrl]).map((url) => kellerImageUrl(url, p.itemNumber)).filter(Boolean),
  )) : [];

  return (
    <div className="container mx-auto px-4 py-6 space-y-4 max-w-6xl">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-2xl font-bold">Keller Marine API Test</h1>
        <Badge variant="outline">Test only · not on live site</Badge>
      </div>

      <Card className="p-4">
        <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); run(); }}>
          <Input value={part} onChange={(e) => setPart(e.target.value)} placeholder="Keller part number" />
          <Button type="submit" disabled={busy}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}<span className="ml-1">Look up</span></Button>
          <Button type="button" variant="outline" disabled={busy || !res} onClick={() => run(true)} title="Bypass 30-min cache"><RefreshCw className="w-4 h-4" /></Button>
        </form>
      </Card>

      {busy && (
        <Card className="p-6 grid md:grid-cols-2 gap-6">
          <Skeleton className="aspect-square w-full" />
          <div className="space-y-3"><Skeleton className="h-8 w-3/4" /><Skeleton className="h-6 w-1/3" /><Skeleton className="h-24 w-full" /></div>
        </Card>
      )}

      {!busy && res?.error && <Card className="p-4 border-destructive text-destructive text-sm">{res.error}</Card>}

      {!busy && p && (
        <Card className="p-6 grid md:grid-cols-2 gap-8">
          <div className="space-y-3">
            <div className="aspect-square bg-muted rounded-md flex items-center justify-center overflow-hidden relative group">
              {images[img] ? (
                <button type="button" className="w-full h-full flex items-center justify-center cursor-zoom-in" onClick={() => setZoom(img)} title="View full size">
                  <img src={images[img]} alt={p.description} className="max-w-full max-h-full object-contain" />
                  <span className="absolute bottom-2 right-2 rounded bg-background/80 p-1.5 opacity-0 group-hover:opacity-100 transition-opacity"><ZoomIn className="w-4 h-4" /></span>
                </button>
              ) : <span className="text-muted-foreground text-sm">No image</span>}
            </div>
            {images.length > 1 && (
              <div className="flex gap-2 flex-wrap">
                {images.map((u, i) => (
                  <button key={u} onClick={() => setImg(i)} className={`w-16 h-16 border rounded overflow-hidden ${i === img ? "ring-2 ring-primary" : ""}`}>
                    <img src={u} alt="" className="w-full h-full object-contain" />
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="space-y-4">
            <h2 className="text-2xl font-bold leading-tight">{p.description || p.itemNumber}</h2>
            <div className="text-sm text-muted-foreground space-y-0.5">
              <div>Item #: <span className="text-foreground font-medium">{p.itemNumber}</span></div>
              {p.manufacturerPartNumber && <div>Mfg Part #: <span className="text-foreground">{p.manufacturerPartNumber}</span></div>}
              {p.upcs.length > 0 && <div>UPC: <span className="text-foreground">{p.upcs.join(", ")}</span></div>}
              {p.status && <div>Status: <span className="text-foreground">{p.status}</span></div>}
            </div>
            <div className="flex items-end gap-6">
              <div><div className="text-xs text-muted-foreground">MSRP</div><div className="text-3xl font-bold text-primary">{money(p.listPrice)}</div></div>
              <div><div className="text-xs text-muted-foreground">Dealer cost (admin)</div><div className="text-lg font-semibold">{money(p.dealerCost)}</div></div>
            </div>
            <Badge variant={p.availableQuantity > 0 ? "default" : "destructive"}>
              {p.availableQuantity > 0 ? `In stock · ${p.availableQuantity} available` : "Out of stock"}
            </Badge>
            {p.longDescription && <div className="text-sm whitespace-pre-line border-t pt-4">{p.longDescription}</div>}
            <div className="border-t pt-4">
              <h3 className="font-semibold text-sm mb-2">Warehouse inventory</h3>
              <table className="w-full text-sm">
                <tbody>
                  <tr className="border-b"><td className="py-1">Default warehouse</td><td className="text-right">{p.warehouseQuantity}</td></tr>
                  {p.warehouses.map((w, i) => <tr key={i} className="border-b"><td className="py-1">{w.name}</td><td className="text-right">{w.quantity}</td></tr>)}
                </tbody>
              </table>
            </div>
            <div className="text-xs text-muted-foreground">{res?.cached ? "From 30-min cache" : "Live"} · {new Date(res!.fetchedAt).toLocaleString()}</div>
          </div>
        </Card>
      )}

      <ImageZoomViewer
        photos={images.map((src) => ({ src }))}
        index={zoom}
        onIndexChange={setZoom}
        title={p?.description || "Keller product image"}
      />

      {!busy && res?.rawXml && (
        <Card className="p-4">
          <Button variant="ghost" size="sm" onClick={() => setShowRaw((v) => !v)}>{showRaw ? "Hide" : "Show"} raw XML response</Button>
          {showRaw && <pre className="mt-2 text-xs overflow-auto max-h-96 bg-muted p-3 rounded">{res.rawXml}</pre>}
        </Card>
      )}
    </div>
  );
}
